import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Image,
  Keyboard,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import {
  Text,
  Card,
  Button,
  TextInput,
  Portal,
  Modal,
  IconButton,
  Avatar,
} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useAppColors } from '../../hooks/useAppColors';
import { responsiveContentStyle } from '../../hooks/useResponsiveLayout';
import {
  usePickupPersons,
  useCreatePickupPerson,
  useUpdatePickupPerson,
  useDeletePickupPerson,
  useUploadPickupPhoto,
} from '../../hooks/usePickupPersons';
import { PickupPerson, PickupPersonInput } from '../../api/pickupPersons';
import { useImagePicker, ImagePickerResult } from '../../hooks/useImagePicker';
import { getProfilePictureUrl } from '../../api/users';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../../components/common/LoadingIndicator';
import ScreenHeader from '../../components/common/ScreenHeader';

interface PickupPersonFormData extends PickupPersonInput {
  id?: string;
}

export default function PickupPersonsScreen() {
  const { t } = useTranslation();
  const navigation = useNavigation<any>();
  const insets = useSafeAreaInsets();
  const colors = useAppColors();
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const modalWidth = Math.min(windowWidth * 0.9, 440);
  
  const [showModal, setShowModal] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [editingPerson, setEditingPerson] = useState<PickupPerson | null>(null);
  const [errors, setErrors] = useState<{ name?: string; phone?: string; relationship_label?: string; emergency_contact_name?: string; emergency_contact_phone?: string; photo?: string }>({});
  const [pendingPhotoResult, setPendingPhotoResult] = useState<ImagePickerResult | null>(null);
  const [formData, setFormData] = useState<PickupPersonFormData>({
    name: '',
    phone: '',
    relationship_label: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
  });

  useEffect(() => {
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const showSub = Keyboard.addListener(showEvent, (e) =>
      setKeyboardHeight(e.endCoordinates?.height ?? 0)
    );
    const hideSub = Keyboard.addListener(hideEvent, () => setKeyboardHeight(0));
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const { data: pickupPersons, isLoading } = usePickupPersons();
  const createMutation = useCreatePickupPerson();
  const updateMutation = useUpdatePickupPerson();
  const deleteMutation = useDeletePickupPerson();
  const uploadMutation = useUploadPickupPhoto();
  const { pickAndPrepareImage } = useImagePicker();

  const handlePickFormPhoto = useCallback(async () => {
    try {
      const result = await pickAndPrepareImage();
      if (result) {
        setPendingPhotoResult(result);
      }
    } catch {
      Alert.alert(t('common.error'), t('pickup.photoUploadError'));
    }
  }, [pickAndPrepareImage, t]);

  const handleAdd = () => {
    setEditingPerson(null);
    setErrors({});
    setPendingPhotoResult(null);
    setFormData({
      name: '',
      phone: '',
      relationship_label: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
    });
    setShowModal(true);
  };

  const handleEdit = (person: PickupPerson) => {
    setEditingPerson(person);
    setErrors({});
    setPendingPhotoResult(null);
    setFormData({
      name: person.name,
      phone: person.phone,
      relationship_label: person.relationship_label || '',
      emergency_contact_name: person.emergency_contact_name || '',
      emergency_contact_phone: person.emergency_contact_phone || '',
    });
    setShowModal(true);
  };

  const handleDelete = (person: PickupPerson) => {
    Alert.alert(
      t('pickup.deleteConfirm'),
      t('pickup.deleteConfirmMessage'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: () => deleteMutation.mutate(person.id),
        },
      ]
    );
  };

  const handleSubmit = () => {
    const newErrors: typeof errors = {};
    if (!formData.name.trim()) newErrors.name = t('pickup.nameRequired');
    if (!formData.phone.trim()) newErrors.phone = t('pickup.phoneRequired');
    if (!formData.relationship_label?.trim()) newErrors.relationship_label = t('pickup.fieldRequired');
    if (!formData.emergency_contact_name?.trim()) newErrors.emergency_contact_name = t('pickup.fieldRequired');
    if (!formData.emergency_contact_phone?.trim()) newErrors.emergency_contact_phone = t('pickup.fieldRequired');
    if (!pendingPhotoResult && !editingPerson?.photo_url) newErrors.photo = t('pickup.photoRequired');
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const submitData = {
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      relationship_label: formData.relationship_label!.trim(),
      emergency_contact_name: formData.emergency_contact_name!.trim(),
      emergency_contact_phone: formData.emergency_contact_phone!.trim(),
    };

    if (editingPerson) {
      updateMutation.mutate({ id: editingPerson.id, data: submitData });
      if (pendingPhotoResult) {
        uploadMutation.mutate({ id: editingPerson.id, formData: pendingPhotoResult.formData });
      }
    } else {
      createMutation.mutate({ data: submitData, photoFormData: pendingPhotoResult!.formData });
    }
    setShowModal(false);
  };

  const renderPickupPerson = (person: PickupPerson) => (
    <Card key={person.id} style={[styles.card, { backgroundColor: colors.card }]}>
      <Card.Content>
        <View style={styles.cardHeader}>
          <View style={styles.avatarContainer}>
            {person.photo_url ? (
              <Avatar.Image
                size={60}
                source={{ uri: getProfilePictureUrl(person.photo_url, person.updated_at) }}
              />
            ) : (
              <Avatar.Icon size={60} icon="account" />
            )}
          </View>
          
          <View style={styles.personInfo}>
            <Text style={[styles.name, { color: colors.text }]}>{person.name}</Text>
            {person.relationship_label && (
              <Text style={[styles.relationship, { color: colors.textSecondary }]}>
                {person.relationship_label}
              </Text>
            )}
            <Text style={[styles.phone, { color: colors.textSecondary }]}>
              📞 {person.phone}
            </Text>
            {person.emergency_contact_name && (
              <Text style={[styles.emergency, { color: colors.textSecondary }]}>
                🚨 {t('pickup.emergency')}: {person.emergency_contact_name}
                {person.emergency_contact_phone && ` (${person.emergency_contact_phone})`}
              </Text>
            )}
          </View>
          
          <View style={styles.actions}>
            <IconButton
              icon="pencil"
              size={20}
              iconColor={colors.text}
              onPress={() => handleEdit(person)}
            />
            <IconButton
              icon="delete"
              size={20}
              iconColor={colors.error}
              onPress={() => handleDelete(person)}
            />
          </View>
        </View>
      </Card.Content>
    </Card>
  );

  return (
    <View style={[styles.container, { backgroundColor: colors.bg, paddingTop: insets.top }]}>
      <ScreenHeader title={t('pickup.title')} onBack={() => navigation.goBack()} />
      
      <ScrollView style={responsiveContentStyle} contentContainerStyle={styles.scrollContent}>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          {t('pickup.subtitle')}
        </Text>

        {isLoading ? (
          <LoadingIndicator />
        ) : pickupPersons && pickupPersons.length > 0 ? (
          pickupPersons.map(renderPickupPerson)
        ) : (
          <View style={styles.emptyState}>
            <Icon name="car-child-seat" size={64} color={colors.textSecondary} />
            <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
              {t('pickup.empty')}
            </Text>
          </View>
        )}

        <Button
          mode="contained"
          icon="plus"
          onPress={handleAdd}
          style={styles.addButton}
        >
          {t('pickup.addPerson')}
        </Button>
      </ScrollView>

      <Portal>
        <Modal
          visible={showModal}
          onDismiss={() => setShowModal(false)}
          contentContainerStyle={[
            styles.modal,
            {
              backgroundColor: colors.bg,
              width: modalWidth,
              marginBottom: keyboardHeight ? keyboardHeight + 20 : 20,
              maxHeight: keyboardHeight
                ? windowHeight - keyboardHeight - insets.top - 40
                : windowHeight * 0.85,
            },
          ]}
        >
          <Text style={[styles.modalTitle, { color: colors.text }]}>
            {editingPerson ? t('pickup.editPerson') : t('pickup.addPerson')}
          </Text>

          <ScrollView style={styles.modalScroll} contentContainerStyle={styles.modalScrollContent} keyboardShouldPersistTaps="handled">
          <TouchableOpacity
            onPress={() => {
              handlePickFormPhoto();
              if (errors.photo) setErrors({ ...errors, photo: undefined });
            }}
            style={styles.photoPickerContainer}
          >
            {pendingPhotoResult ? (
              <Image source={{ uri: pendingPhotoResult.uri }} style={styles.photoPreview} />
            ) : editingPerson?.photo_url ? (
              <Image
                source={{ uri: getProfilePictureUrl(editingPerson.photo_url, editingPerson.updated_at) }}
                style={styles.photoPreview}
              />
            ) : (
              <View style={[styles.photoPlaceholder, { backgroundColor: colors.card, borderColor: errors.photo ? colors.error : colors.border }]}>
                <Icon name="camera-plus" size={32} color={errors.photo ? colors.error : colors.textSecondary} />
                <Text style={[styles.photoPlaceholderText, { color: errors.photo ? colors.error : colors.textSecondary }]}>
                  {t('pickup.addPhoto')}
                </Text>
              </View>
            )}
            {(pendingPhotoResult || editingPerson?.photo_url) && (
              <View style={styles.photoEditBadge}>
                <Icon name="camera" size={14} color="#fff" />
              </View>
            )}
          </TouchableOpacity>
          {errors.photo && (
            <Text style={[styles.errorText, { color: colors.error, textAlign: 'center', marginBottom: 8 }]}>{errors.photo}</Text>
          )}

          <TextInput
            label={`${t('pickup.name')} *`}
            value={formData.name}
            onChangeText={(text) => {
              setFormData({ ...formData, name: text });
              if (errors.name) setErrors({ ...errors, name: undefined });
            }}
            style={styles.input}
            mode="outlined"
            error={!!errors.name}
          />
          {errors.name && (
            <Text style={[styles.errorText, { color: colors.error }]}>{errors.name}</Text>
          )}

          <TextInput
            label={`${t('pickup.phone')} *`}
            value={formData.phone}
            onChangeText={(text) => {
              setFormData({ ...formData, phone: text });
              if (errors.phone) setErrors({ ...errors, phone: undefined });
            }}
            style={styles.input}
            mode="outlined"
            keyboardType="phone-pad"
            error={!!errors.phone}
          />
          {errors.phone && (
            <Text style={[styles.errorText, { color: colors.error }]}>{errors.phone}</Text>
          )}

          <TextInput
            label={`${t('pickup.relationship')} *`}
            value={formData.relationship_label || ''}
            onChangeText={(text) => {
              setFormData({ ...formData, relationship_label: text });
              if (errors.relationship_label) setErrors({ ...errors, relationship_label: undefined });
            }}
            style={styles.input}
            mode="outlined"
            placeholder={t('pickup.relationshipPlaceholder')}
            error={!!errors.relationship_label}
          />
          {errors.relationship_label && (
            <Text style={[styles.errorText, { color: colors.error }]}>{errors.relationship_label}</Text>
          )}

          <TextInput
            label={`${t('pickup.emergencyName')} *`}
            value={formData.emergency_contact_name || ''}
            onChangeText={(text) => {
              setFormData({ ...formData, emergency_contact_name: text });
              if (errors.emergency_contact_name) setErrors({ ...errors, emergency_contact_name: undefined });
            }}
            style={styles.input}
            mode="outlined"
            error={!!errors.emergency_contact_name}
          />
          {errors.emergency_contact_name && (
            <Text style={[styles.errorText, { color: colors.error }]}>{errors.emergency_contact_name}</Text>
          )}

          <TextInput
            label={`${t('pickup.emergencyPhone')} *`}
            value={formData.emergency_contact_phone || ''}
            onChangeText={(text) => {
              setFormData({ ...formData, emergency_contact_phone: text });
              if (errors.emergency_contact_phone) setErrors({ ...errors, emergency_contact_phone: undefined });
            }}
            style={styles.input}
            mode="outlined"
            keyboardType="phone-pad"
            error={!!errors.emergency_contact_phone}
          />
          {errors.emergency_contact_phone && (
            <Text style={[styles.errorText, { color: colors.error }]}>{errors.emergency_contact_phone}</Text>
          )}

          </ScrollView>

          <View style={styles.modalActions}>
            <Button
              mode="text"
              onPress={() => setShowModal(false)}
              style={styles.cancelButton}
            >
              {t('common.cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleSubmit}
              loading={createMutation.isPending || updateMutation.isPending}
              style={styles.saveButton}
            >
              {t('common.save')}
            </Button>
          </View>
        </Modal>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 16,
    textAlign: 'center',
  },
  card: {
    marginBottom: 12,
    elevation: 2,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  avatarContainer: {
    marginRight: 16,
    position: 'relative',
  },
  cameraIcon: {
    position: 'absolute',
    bottom: -4,
    right: -4,
    backgroundColor: 'rgba(0,0,0,0.6)',
    margin: 0,
  },
  personInfo: {
    flex: 1,
  },
  name: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  relationship: {
    fontSize: 14,
    marginBottom: 2,
  },
  phone: {
    fontSize: 13,
    marginBottom: 2,
  },
  emergency: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  actions: {
    flexDirection: 'row',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  emptyText: {
    fontSize: 16,
    marginTop: 16,
    textAlign: 'center',
  },
  addButton: {
    marginTop: 16,
  },
  photoPickerContainer: {
    alignSelf: 'center',
    marginBottom: 20,
    position: 'relative',
  },
  photoPreview: {
    width: 80,
    height: 80,
    borderRadius: 40,
  },
  photoPlaceholder: {
    width: 80,
    height: 80,
    borderRadius: 40,
    borderWidth: 2,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
  photoPlaceholderText: {
    fontSize: 10,
    marginTop: 2,
    textAlign: 'center',
  },
  photoEditBadge: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    borderRadius: 12,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modal: {
    margin: 20,
    padding: 20,
    borderRadius: 12,
    alignSelf: 'center',
  },
  modalScroll: {
    flexShrink: 1,
  },
  modalScrollContent: {
    paddingBottom: 8,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 20,
    textAlign: 'center',
  },
  input: {
    marginBottom: 4,
  },
  errorText: {
    fontSize: 12,
    marginBottom: 12,
    marginLeft: 8,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  cancelButton: {
    flex: 1,
    marginRight: 8,
  },
  saveButton: {
    flex: 1,
    marginLeft: 8,
  },
});